import { Module, VuexModule, Mutation, Action, getModule } from "vuex-module-decorators";
import { formatBool, formatDate, formatInventorySummaryType, formatUoMQuantity } from "../../../../utils";
import { TableField, PageData } from "../../../types";
import {
    GetInventorySummaryRequest,
    InventorySummaryListFilters,
    InventorySummaryResource,
    InventorySummaryListItem
  } from "../../../../models/api/barcodeSystem/inventory/summary";
import {
  GetInventoryItemsRequest,
    InventoryItemListFilters,
    InventoryItemResource,
    InventoryItemListItem,
    UpdateInventoryItemRequest,
    ResolveMissingInventoryItemRequest,
    DeleteInventoryItemRequest
  } from "../../../../models/api/barcodeSystem/inventory/inventoryItems";
import {
  GetPalletsRequest,
  PalletListFilters,
  PalletResource,
  PalletListItem
  } from "../../../../models/api/barcodeSystem/inventory/pallets";
import { BarcodeOrderListItem } from "../../../../models/api/barcodeSystem/barcodeOrders/get";
import {
    InventoryTransactionListItem
  } from "../../../../models/api/barcodeSystem/inventory/inventoryTransactions";
import Store from "../../../index";
import { rawApi, api } from "../../../../lib";
import { AxiosResponse } from "axios";
import { defaultSummaryFilters, defaultItemFilters, defaultPalletFilters, defaultBulkFilters } from "./helpers";
import BarcodeSystemModule from "@/store/modules/barcodeSystem";
import * as modals from "@/lib/modals";
import { BulkInventoryItemListFilters, BulkInventoryItemListItem, BulkInventoryItemResource, GetBulkInventoryItemsRequest } from "../../../../models/api/barcodeSystem/inventory/bulkInventoryItems";
import { LinkInventoryRequest, UnspecifiedItem } from "../../../../models/api/barcodeSystem/barcodeOrders/linkInventory";
import { AvailableQuantityRequest, AvailableQuantityResponse } from "../../../../models/api/barcodeSystem/inventory/getAvailableQuantity";
import { ModelCubesResource, SaveCubesRequest } from "@/models/api/barcodeSystem/inventory/cubes";
import { ModelLinkSummaryResource } from "../../../../models/api/barcodeSystem/inventory/modelLinkSummary";

const name = "inventory";
const barcodeSystemModuleState = getModule(BarcodeSystemModule);

if (Store.state[name]) {
  Store.unregisterModule(name);
}

@Module({ name: name, dynamic: true, store: Store, namespaced: true })
export default class InventoryModule extends VuexModule {
    public summaryData?: InventorySummaryListItem[] = null;
    public summaryFilters: InventorySummaryListFilters = defaultSummaryFilters();
    public summaryDataSortKey: string = null;
    public summaryDataSortAscending: boolean = true;
    public summaryPageData: PageData = {
      totalPages: null,
      totalItems: null,
      currentPage: 1,
      pageSize: 30
    };
    public summaryFields: Array<TableField> = [
        { key: "selected", label: "", hidden: false },
        { key: "type", sortable: true, label: "Type", formatter: formatInventorySummaryType, hidden: false },
        { key: "facilityCode", sortable: true, label: "Facility", hidden: false },
        { key: "warehouseLocationCode", sortable: true, label: "Location", hidden: false, noWrap: true },
        { key: "companyName", sortable: true, label: "Company", hidden: false },
        { key: "manufacturerCode", sortable: true, label: "Manufacturer", hidden: false },
        { key: "itemType", sortable: true, label: "Commodity", hidden: false },
        { key: "modelNumber", sortable: true, label: "Model #", hidden: false, noWrap: true },
        { key: "sku", sortable: true, label: "SKU", hidden: false, noWrap: true },
        { key: "itemDescription", sortable: true, label: "Description", hidden: false },
        { key: "poNumber", sortable: true, label: "PO #", hidden: false, noWrap: true },
        { key: "serialNumber", sortable: true, label: "Serial #", hidden: false, noWrap: true },
        { key: "palletId", sortable: true, label: "Pallet Id", hidden: false },
        { key: "quantity", sortable: true, label: "Qty", hidden: false },
        { key: "status", sortable: true, label: "Status", hidden: false},
        { key: "orderNumber", sortable: true, label: "Order #", hidden: false},
        { key: "crn", sortable: true, label: "CRN", hidden: false, noWrap: true},
        { key: "damaged", sortable: true, label: "Is Damaged?", formatter: formatBool, hidden: false},
        { key: "returned", sortable: true, label: "Is Returned?", formatter: formatBool, hidden: false},
        { key: "receivedDate", sortable: true, label: "Rec Date", formatter: formatDate, hidden: false, noWrap: true},
        { key: "loadedDate", sortable: true, label: "Loaded Date", formatter: formatDate, hidden: false, noWrap: true},
        { key: "deliveredDate", sortable: true, label: "Del Date", formatter: formatDate, hidden: false, noWrap: true}
    ];
    public itemData?: InventoryItemListItem[] = null;
    public linkItemData?: InventoryItemListItem[] = null;
    public itemFilters: InventoryItemListFilters = defaultItemFilters();
    public linkItemFilters: InventoryItemListFilters = defaultItemFilters();
    public itemDataSortKey: string = null;
    public linkItemDataSortKey: string = null;
    public itemDataSortAscending: boolean = true;
    public linkItemDataSortAscending: boolean = true;
    public itemPageData: PageData = {
      totalPages: null,
      totalItems: null,
      currentPage: 1,
      pageSize: 30
    };
    public linkItemPageData: PageData = {
      totalPages: null,
      totalItems: null,
      currentPage: 1,
      pageSize: 30
    };
    public itemFields: Array<TableField> = [
        { key: "selected", label: "", hidden: false },
        { key: "facilityCode", sortable: true, label: "Facility", hidden: false },
        { key: "warehouseLocationCode", sortable: true, label: "Location", hidden: false, noWrap: true },
        { key: "company.companyName", sortable: true, label: "Company", hidden: false },
        { key: "displayManufacturerCode", sortable: true, label: "Manufacturer", hidden: false },
        { key: "itemType", sortable: true, label: "Commodity", hidden: false },
        { key: "modelNumber", sortable: true, label: "Model #", hidden: false, noWrap: true },
        { key: "sku", sortable: true, label: "SKU", hidden: false, noWrap: true },
        { key: "itemDescription", sortable: true, label: "Description", hidden: false },
        { key: "itemScan", sortable: true, label: "Barcode", hidden: false },
        { key: "poNumber", sortable: true, label: "PO #", hidden: false, noWrap: true },
        { key: "serialNumber", sortable: true, label: "Serial #", hidden: false, noWrap: true },
        { key: "palletId", sortable: true, label: "Pallet Id", hidden: false },
        { key: "quantity", sortable: true, label: "Qty", hidden: false },
        { key: "status", sortable: true, label: "Status", hidden: false},
        { key: "orderNumber", sortable: true, label: "Order #", hidden: false},
        { key: "crn", sortable: true, label: "CRN", hidden: false, noWrap: true},
        { key: "loadName", sortable: true, label: "Load Name", hidden: false, noWrap: true},
        { key: "isDamaged", sortable: true, label: "Is Damaged?", formatter: formatBool, hidden: false},
        { key: "returned", sortable: true, label: "Is Returned?", formatter: formatBool, hidden: false},
        { key: "receivedDate", sortable: true, label: "Rec Date", formatter: formatDate, hidden: false, noWrap: true},
        { key: "loadedDate", sortable: true, label: "Loaded Date", formatter: formatDate, hidden: false, noWrap: true},
        { key: "deliveredDate", sortable: true, label: "Del Date", formatter: formatDate, hidden: false, noWrap: true}
    ];
    public unspecifiedItemFields: Array<TableField> = [
        { key: "selected", label: "", hidden: false },
        { key: "facilityCode", sortable: true, label: "Facility", hidden: false },
        { key: "companyName", sortable: true, label: "Company", hidden: false },
        { key: "manufacturerCode", sortable: true, label: "Manufacturer", hidden: false },
        { key: "modelNumber", sortable: true, label: "Model #", hidden: false, noWrap: true },
        { key: "poNumber", sortable: true, label: "PO #", hidden: false, noWrap: true }
    ];
    public currentInventoryItem: InventoryItemResource = null;
    public palletData?: PalletListItem[] = null;
    public palletFilters: PalletListFilters = defaultPalletFilters();
    public palletDataSortKey: string = null;
    public palletDataSortAscending: boolean = true;
    public palletPageData: PageData = {
      totalPages: null,
      totalItems: null,
      currentPage: 1,
      pageSize: 30
    };
    public palletFields: Array<TableField> = [
        { key: "selected", label: "", hidden: false },
        { key: "facilityCode", sortable: true, label: "Facility", hidden: false },
        { key: "warehouseLocationCode", sortable: true, label: "Location", hidden: false, noWrap: true },
        { key: "id", sortable: true, label: "Pallet Id", hidden: false },
        { key: "palletNumber", sortable: true, label: "Pallet#", hidden: false, noWrap: true },
        { key: "statusDescription", sortable: true, label: "Status", hidden: false},
        { key: "isNoScanPallet", sortable: true, label: "Is No Scan Pallet?", formatter: formatBool, hidden: false},
        { key: "createdDate", sortable: true, label: "Created Date", formatter: formatDate, hidden: false, noWrap: true}
    ];
    public currentPallet: PalletResource = null;
    public palletItemsData: InventoryItemListItem[] = null;
    public palletOrdersData: BarcodeOrderListItem[] = null;
    public transData?: InventoryTransactionListItem[] = null;
    public bulkData?: BulkInventoryItemListItem[] = null;
    public bulkFilters: BulkInventoryItemListFilters = defaultBulkFilters();
    public bulkDataSortKey: string = null;
    public bulkDataSortAscending: boolean = true;
    public bulkPageData: PageData = {
      totalPages: null,
      totalItems: null,
      currentPage: 1,
      pageSize: 30
    };
    public bulkFields: Array<TableField> = [
        { key: "selected", label: "", hidden: false },
        { key: "facilityCode", sortable: true, label: "Facility", hidden: false },
        { key: "warehouseLocationCode", sortable: true, label: "Location", hidden: false, noWrap: true },
        { key: "company.companyName", sortable: true, label: "Company", hidden: false },
        { key: "displayManufacturerCode", sortable: true, label: "Manufacturer", hidden: false },
        { key: "itemType", sortable: true, label: "Commodity", hidden: false },
        { key: "modelNumber", sortable: true, label: "Model #", hidden: false, noWrap: true },
        { key: "sku", sortable: true, label: "SKU", hidden: false, noWrap: true },
        { key: "itemDescription", sortable: true, label: "Description", hidden: false },
        { key: "quantity", sortable: true, label: "Qty", hidden: false, formatter: formatUoMQuantity, formatterParameterKey: "stockSku" }
    ]
    public currentBulkInventoryItem: BulkInventoryItemResource = null;
    public otherBulkInventoryItems: BulkInventoryItemListItem[] = null;
    public loading: boolean = false;

    @Action
    async getInventorySummary(params: GetInventorySummaryRequest): Promise<void> {
      const pageSize = params ? params.pageSize : null || this.summaryPageData.pageSize || 30;
      const pageNumber = params ? params.pageNumber : null || this.summaryPageData.currentPage + 1;

      if (pageNumber === 1) {
        this.resetSummaryList();
      }
  
      let target = `/barcode/inventory/?page=${pageNumber}&PageSize=${pageSize}`;
  
      if (this.summaryFilters) {
        if (this.summaryFilters.bulkItemId) {
            target += `&bulkItemId=${this.summaryFilters.bulkItemId}`;
        }
        if (this.summaryFilters.companyIds) {
          this.summaryFilters.companyIds.forEach(element => {
            target += `&companyIds=${element}`;
          });
        }
        if (this.summaryFilters.crn) {
            target += `&crn=${this.summaryFilters.crn}`;
        }
        if (this.summaryFilters.damageCode) {
            target += `&damageCode=${this.summaryFilters.damageCode}`;
        }
        if (this.summaryFilters.damaged !== undefined && this.summaryFilters.damaged != null) {
            target += `&damaged=${this.summaryFilters.damaged}`;
        }
        if (this.summaryFilters.deliveredDate) {
            const dateData = this.summaryFilters.deliveredDate.split(" ~ ");
            if(!dateData[1] || dateData[1] === "null")
            {
                dateData[1] = dateData[0];
            }
            target += `&deliveredDateStart=${dateData[0]}&deliveredDateEnd=${dateData[1]}`;
        }
        if (this.summaryFilters.facilityCodes) {
          this.summaryFilters.facilityCodes.forEach(element => {
            target += `&facilityCodes=${element}`;
          });
        }
        else if(barcodeSystemModuleState.currentFacilityCode)
        {
            target += `&facilityCodes=${barcodeSystemModuleState.currentFacilityCode}`;
        }
        if (this.summaryFilters.id) {
            target += `&id=${this.summaryFilters.id}`;
        }
        if (this.summaryFilters.inventoryItemId) {
            target += `&inventoryItemId=${this.summaryFilters.inventoryItemId}`;
        }
        if (this.summaryFilters.itemDescription) {
            target += `&itemDescription=${this.summaryFilters.itemDescription}`;
        }
        if (this.summaryFilters.itemScan) {
            target += `&itemScan=${this.summaryFilters.itemScan}`;
        }
        if (this.summaryFilters.itemType) {
            target += `&itemType=${this.summaryFilters.itemType}`;
        }
        if (this.summaryFilters.loadedDate) {
            const dateData = this.summaryFilters.loadedDate.split(" ~ ");
            if(!dateData[1] || dateData[1] === "null")
            {
                dateData[1] = dateData[0];
            }
            target += `&loadedDateStart=${dateData[0]}&loadedDateEnd=${dateData[1]}`;
        }
        if (this.summaryFilters.manufacturerCodes) {
          this.summaryFilters.manufacturerCodes.forEach(element => {
            target += `&manufacturerCodes=${element}`;
          });
        }
        if (this.summaryFilters.modelNumber) {
            target += `&modelNumber=${this.summaryFilters.modelNumber}`;
        }
        if (this.summaryFilters.orderId) {
            target += `&orderId=${this.summaryFilters.orderId}`;
        }
        if (this.summaryFilters.orderNumber) {
            target += `&orderNumber=${this.summaryFilters.orderNumber}`;
        }
        if (this.summaryFilters.palletId) {
            target += `&palletId=${this.summaryFilters.palletId}`;
        }
        if (this.summaryFilters.poNumber) {
            target += `&poNumber=${this.summaryFilters.poNumber}`;
        }
        if (this.summaryFilters.quantity) {
            target += `&quantity=${this.summaryFilters.quantity}`;
        }
        if (this.summaryFilters.receivedDate) {
            const dateData = this.summaryFilters.receivedDate.split(" ~ ");
            if(!dateData[1] || dateData[1] === "null")
            {
                dateData[1] = dateData[0];
            }
            target += `&receivedDateStart=${dateData[0]}&receivedDateEnd=${dateData[1]}`;
        }
        if (this.summaryFilters.returnReason) {
            target += `&returnReason=${this.summaryFilters.returnReason}`;
        }
        if (this.summaryFilters.returned !== undefined && this.summaryFilters.returned != null) {
            target += `&returned=${this.summaryFilters.returned}`;
        }
        if (this.summaryFilters.serialNumber) {
            target += `&serialNumber=${this.summaryFilters.serialNumber}`;
        }
        if (this.summaryFilters.shrinkage !== undefined && this.summaryFilters.shrinkage != null) {
            target += `&shrinkage=${this.summaryFilters.shrinkage}`;
        }
        if (this.summaryFilters.sku) {
            target += `&sku=${this.summaryFilters.sku}`;
        }
        if (this.summaryFilters.statuses) {
          this.summaryFilters.statuses.forEach(element => {
            target += `&statuses=${element}`;
          });
        }
        if (this.summaryFilters.type) {
            target += `&type=${this.summaryFilters.type}`;
        }
        if (this.summaryFilters.warehouseLocationId != null) {
            target += `&warehouseLocationId=${this.summaryFilters.warehouseLocationId}`;
        }
      }
      else if(barcodeSystemModuleState.currentFacilityCode)
      {
          target += `&facilityCodes=${barcodeSystemModuleState.currentFacilityCode}`;
      }

      if (this.summaryDataSortKey) {
        target += `&sortField=${this.summaryDataSortKey}&SortAscending=${
          this.summaryDataSortAscending ? "true" : "false"
        }`;
      }
  
      try {
        var response = await rawApi.get<InventorySummaryResource[]>(target);
        this.setSummaryList(response);
        this.setSummaryCurrentPage(pageNumber);
      } catch (ex) {
        throw ex;
      } finally {
        this.setLoading(false);
      }
    }

    @Action
    async getInventoryItems(params: GetInventoryItemsRequest): Promise<void> {
      const pageSize = params ? params.pageSize : null || this.itemPageData.pageSize || 30;
      const pageNumber = params ? params.pageNumber : null || this.itemPageData.currentPage + 1;

      if (pageNumber === 1) {
        this.resetItemList();
      }
  
      let target = `/barcode/inventory/inventoryItems/?page=${pageNumber}&PageSize=${pageSize}`;
  
      if (this.itemFilters) {
        if (this.itemFilters.companyIds) {
          this.itemFilters.companyIds.forEach(element => {
            target += `&companyIds=${element}`;
          });
        }
        if (this.itemFilters.crn) {
            target += `&crn=${this.itemFilters.crn}`;
        }
        if (this.itemFilters.damageCode) {
            target += `&damageCode=${this.itemFilters.damageCode}`;
        }
        if (this.itemFilters.isDamaged !== undefined && this.itemFilters.isDamaged != null) {
            target += `&isDamaged=${this.itemFilters.isDamaged}`;
        }
        if (this.itemFilters.deliveredDate) {
            const dateData = this.itemFilters.deliveredDate.split(" ~ ");
            if(!dateData[1] || dateData[1] === "null")
            {
                dateData[1] = dateData[0];
            }
            target += `&deliveredDateStart=${dateData[0]}&deliveredDateEnd=${dateData[1]}`;
        }
        if (this.itemFilters.facilityCodes) {
          this.itemFilters.facilityCodes.forEach(element => {
            target += `&facilityCodes=${element}`;
          });
        }
        else if(barcodeSystemModuleState.currentFacilityCode)
        {
            target += `&facilityCodes=${barcodeSystemModuleState.currentFacilityCode}`;
        }
        if (this.itemFilters.id) {
            target += `&id=${this.itemFilters.id}`;
        }
        if (this.itemFilters.itemDescription) {
            target += `&itemDescription=${this.itemFilters.itemDescription}`;
        }
        if (this.itemFilters.itemScan) {
            target += `&itemScan=${this.itemFilters.itemScan}`;
        }
        if (this.itemFilters.itemType) {
            target += `&itemType=${this.itemFilters.itemType}`;
        }
        if (this.itemFilters.loadedDate) {
            const dateData = this.itemFilters.loadedDate.split(" ~ ");
            if(!dateData[1] || dateData[1] === "null")
            {
                dateData[1] = dateData[0];
            }
            target += `&loadedDateStart=${dateData[0]}&loadedDateEnd=${dateData[1]}`;
        }
        if (this.itemFilters.manufacturerCodes) {
          this.itemFilters.manufacturerCodes.forEach(element => {
            target += `&manufacturerCodes=${element}`;
          });
        }
        if (this.itemFilters.modelNumber) {
            target += `&modelNumber=${this.itemFilters.modelNumber}`;
        }
        if (this.itemFilters.orderId) {
            target += `&orderId=${this.itemFilters.orderId}`;
        }
        if (this.itemFilters.orderNumber) {
            target += `&orderNumber=${this.itemFilters.orderNumber}`;
        }
        if (this.itemFilters.loadName) {
            target += `&loadName=${this.itemFilters.loadName}`;
        }
        if (this.itemFilters.palletId) {
            target += `&palletId=${this.itemFilters.palletId}`;
        }
        if (this.itemFilters.poNumber) {
            target += `&poNumber=${this.itemFilters.poNumber}`;
        }
        if (this.itemFilters.quantity) {
            target += `&quantity=${this.itemFilters.quantity}`;
        }
        if (this.itemFilters.receivedDate) {
            const dateData = this.itemFilters.receivedDate.split(" ~ ");
            if(!dateData[1] || dateData[1] === "null")
            {
                dateData[1] = dateData[0];
            }
            target += `&receivedDateStart=${dateData[0]}&receivedDateEnd=${dateData[1]}`;
        }
        if (this.itemFilters.returnReason) {
            target += `&returnReason=${this.itemFilters.returnReason}`;
        }
        if (this.itemFilters.returned !== undefined && this.itemFilters.returned != null) {
            target += `&returned=${this.itemFilters.returned}`;
        }
        if (this.itemFilters.serialNumber) {
            target += `&serialNumber=${this.itemFilters.serialNumber}`;
        }
        if (this.itemFilters.shrinkage !== undefined && this.itemFilters.shrinkage != null) {
            target += `&shrinkage=${this.itemFilters.shrinkage}`;
        }
        if (this.itemFilters.sku) {
            target += `&sku=${this.itemFilters.sku}`;
        }
        if (this.itemFilters.statuses) {
          this.itemFilters.statuses.forEach(element => {
            target += `&statuses=${element}`;
          });
        }
        if (this.itemFilters.warehouseLocationId != null) {
            target += `&warehouseLocationId=${this.itemFilters.warehouseLocationId}`;
        }
        if (this.itemFilters.isOpen !== undefined && this.itemFilters.isOpen != null) {
            target += `&isOpen=${this.itemFilters.isOpen}`;
        }
        if (this.itemFilters.isAvailable !== undefined && this.itemFilters.isAvailable != null) {
            target += `&isOpen=${this.itemFilters.isAvailable}`;
        }
        if (this.itemFilters.canBeAddedToOrder !== undefined && this.itemFilters.canBeAddedToOrder != null) {
            target += `&canBeAddedToOrder=${this.itemFilters.canBeAddedToOrder}`;
        }
      }
      else if(barcodeSystemModuleState.currentFacilityCode)
      {
          target += `&facilityCodes=${barcodeSystemModuleState.currentFacilityCode}`;
      }

      if (this.itemDataSortKey) {
        target += `&sortField=${this.itemDataSortKey}&SortAscending=${
          this.itemDataSortAscending ? "true" : "false"
        }`;
      }
  
      try {
        var response = await rawApi.get<InventoryItemListItem[]>(target);
        this.setItemList(response);
        this.setItemCurrentPage(pageNumber);
      } catch (ex) {
        throw ex;
      } finally {
        this.setLoading(false);
      }
    }

    @Action
    async getInventoryItemsExport(params: GetInventoryItemsRequest): Promise<void> {
      const pageSize = params ? params.pageSize : null || this.itemPageData.pageSize || 5000;
      const pageNumber = params ? params.pageNumber : null || this.itemPageData.currentPage + 1;
  
      let target = `/barcode/inventory/inventoryItems/?page=${pageNumber}&PageSize=${pageSize}`;
  
      if (this.itemFilters) {
        if (this.itemFilters.companyIds) {
          this.itemFilters.companyIds.forEach(element => {
            target += `&companyIds=${element}`;
          });
        }
        if (this.itemFilters.crn) {
            target += `&crn=${this.itemFilters.crn}`;
        }
        if (this.itemFilters.damageCode) {
            target += `&damageCode=${this.itemFilters.damageCode}`;
        }
        if (this.itemFilters.isDamaged !== undefined && this.itemFilters.isDamaged != null) {
            target += `&isDamaged=${this.itemFilters.isDamaged}`;
        }
        if (this.itemFilters.deliveredDate) {
            const dateData = this.itemFilters.deliveredDate.split(" ~ ");
            if(!dateData[1] || dateData[1] === "null")
            {
                dateData[1] = dateData[0];
            }
            target += `&deliveredDateStart=${dateData[0]}&deliveredDateEnd=${dateData[1]}`;
        }
        if (this.itemFilters.facilityCodes) {
          this.itemFilters.facilityCodes.forEach(element => {
            target += `&facilityCodes=${element}`;
          });
        }
        else if(barcodeSystemModuleState.currentFacilityCode)
        {
            target += `&facilityCodes=${barcodeSystemModuleState.currentFacilityCode}`;
        }
        if (this.itemFilters.id) {
            target += `&id=${this.itemFilters.id}`;
        }
        if (this.itemFilters.itemDescription) {
            target += `&itemDescription=${this.itemFilters.itemDescription}`;
        }
        if (this.itemFilters.itemScan) {
            target += `&itemScan=${this.itemFilters.itemScan}`;
        }
        if (this.itemFilters.itemType) {
            target += `&itemType=${this.itemFilters.itemType}`;
        }
        if (this.itemFilters.loadedDate) {
            const dateData = this.itemFilters.loadedDate.split(" ~ ");
            if(!dateData[1] || dateData[1] === "null")
            {
                dateData[1] = dateData[0];
            }
            target += `&loadedDateStart=${dateData[0]}&loadedDateEnd=${dateData[1]}`;
        }
        if (this.itemFilters.manufacturerCodes) {
          this.itemFilters.manufacturerCodes.forEach(element => {
            target += `&manufacturerCodes=${element}`;
          });
        }
        if (this.itemFilters.modelNumber) {
            target += `&modelNumber=${this.itemFilters.modelNumber}`;
        }
        if (this.itemFilters.orderId) {
            target += `&orderId=${this.itemFilters.orderId}`;
        }
        if (this.itemFilters.orderNumber) {
            target += `&orderNumber=${this.itemFilters.orderNumber}`;
        }
        if (this.itemFilters.loadName) {
            target += `&loadName=${this.itemFilters.loadName}`;
        }
        if (this.itemFilters.palletId) {
            target += `&palletId=${this.itemFilters.palletId}`;
        }
        if (this.itemFilters.poNumber) {
            target += `&poNumber=${this.itemFilters.poNumber}`;
        }
        if (this.itemFilters.quantity) {
            target += `&quantity=${this.itemFilters.quantity}`;
        }
        if (this.itemFilters.receivedDate) {
            const dateData = this.itemFilters.receivedDate.split(" ~ ");
            if(!dateData[1] || dateData[1] === "null")
            {
                dateData[1] = dateData[0];
            }
            target += `&receivedDateStart=${dateData[0]}&receivedDateEnd=${dateData[1]}`;
        }
        if (this.itemFilters.returnReason) {
            target += `&returnReason=${this.itemFilters.returnReason}`;
        }
        if (this.itemFilters.returned !== undefined && this.itemFilters.returned != null) {
            target += `&returned=${this.itemFilters.returned}`;
        }
        if (this.itemFilters.serialNumber) {
            target += `&serialNumber=${this.itemFilters.serialNumber}`;
        }
        if (this.itemFilters.shrinkage !== undefined && this.itemFilters.shrinkage != null) {
            target += `&shrinkage=${this.itemFilters.shrinkage}`;
        }
        if (this.itemFilters.sku) {
            target += `&sku=${this.itemFilters.sku}`;
        }
        if (this.itemFilters.statuses) {
          this.itemFilters.statuses.forEach(element => {
            target += `&statuses=${element}`;
          });
        }
        if (this.itemFilters.warehouseLocationId != null) {
            target += `&warehouseLocationId=${this.itemFilters.warehouseLocationId}`;
        }
        if (this.itemFilters.isOpen !== undefined && this.itemFilters.isOpen != null) {
            target += `&isOpen=${this.itemFilters.isOpen}`;
        }
        if (this.itemFilters.isAvailable !== undefined && this.itemFilters.isAvailable != null) {
            target += `&isOpen=${this.itemFilters.isAvailable}`;
        }
        if (this.itemFilters.canBeAddedToOrder !== undefined && this.itemFilters.canBeAddedToOrder != null) {
            target += `&canBeAddedToOrder=${this.itemFilters.canBeAddedToOrder}`;
        }
      }
      else if(barcodeSystemModuleState.currentFacilityCode)
      {
          target += `&facilityCodes=${barcodeSystemModuleState.currentFacilityCode}`;
      }

      if (this.itemDataSortKey) {
        target += `&sortField=${this.itemDataSortKey}&SortAscending=${
          this.itemDataSortAscending ? "true" : "false"
        }`;
      }
  
      try {
        var response = await rawApi.get<InventoryItemListItem[]>(target);
        this.resetItemList();
        this.setItemList(response);
      } catch (ex) {
        throw ex;
      } finally {
        this.setLoading(false);
      }
    }

    @Action
    async getInventoryItemsForLinking(params: GetInventoryItemsRequest): Promise<void> {
      const pageSize = params ? params.pageSize : null || this.linkItemPageData.pageSize || 30;
      const pageNumber = params ? params.pageNumber : null || this.linkItemPageData.currentPage + 1;

      if (pageNumber === 1) {
        this.resetLinkItemList();
      }
  
      let target = `/barcode/inventory/inventoryItems/?page=${pageNumber}&PageSize=${pageSize}`;
  
      if (this.linkItemFilters) {
        if (this.linkItemFilters.companyIds) {
          this.linkItemFilters.companyIds.forEach(element => {
            target += `&companyIds=${element}`;
          });
        }
        if (this.linkItemFilters.crn) {
            target += `&crn=${this.linkItemFilters.crn}`;
        }
        if (this.linkItemFilters.damageCode) {
            target += `&damageCode=${this.linkItemFilters.damageCode}`;
        }
        if (this.linkItemFilters.isDamaged !== undefined && this.linkItemFilters.isDamaged != null) {
            target += `&isDamaged=${this.linkItemFilters.isDamaged}`;
        }
        if (this.linkItemFilters.deliveredDate) {
            const dateData = this.linkItemFilters.deliveredDate.split(" ~ ");
            if(!dateData[1] || dateData[1] === "null")
            {
                dateData[1] = dateData[0];
            }
            target += `&deliveredDateStart=${dateData[0]}&deliveredDateEnd=${dateData[1]}`;
        }
        if (this.linkItemFilters.facilityCodes) {
          this.linkItemFilters.facilityCodes.forEach(element => {
            target += `&facilityCodes=${element}`;
          });
        }
        if (this.linkItemFilters.id) {
            target += `&id=${this.linkItemFilters.id}`;
        }
        if (this.linkItemFilters.itemDescription) {
            target += `&itemDescription=${this.linkItemFilters.itemDescription}`;
        }
        if (this.linkItemFilters.itemScan) {
            target += `&itemScan=${this.linkItemFilters.itemScan}`;
        }
        if (this.linkItemFilters.itemType) {
            target += `&itemType=${this.linkItemFilters.itemType}`;
        }
        if (this.linkItemFilters.loadedDate) {
            const dateData = this.linkItemFilters.loadedDate.split(" ~ ");
            if(!dateData[1] || dateData[1] === "null")
            {
                dateData[1] = dateData[0];
            }
            target += `&loadedDateStart=${dateData[0]}&loadedDateEnd=${dateData[1]}`;
        }
        if (this.linkItemFilters.manufacturerCodes) {
          this.linkItemFilters.manufacturerCodes.forEach(element => {
            target += `&manufacturerCodes=${element}`;
          });
        }
        if (this.linkItemFilters.modelNumber) {
            target += `&modelNumber=${this.linkItemFilters.modelNumber}`;
        }
        if (this.linkItemFilters.orderId) {
            target += `&orderId=${this.linkItemFilters.orderId}`;
        }
        if (this.linkItemFilters.orderNumber) {
            target += `&orderNumber=${this.linkItemFilters.orderNumber}`;
        }
        if (this.linkItemFilters.loadName) {
            target += `&loadName=${this.linkItemFilters.loadName}`;
        }
        if (this.linkItemFilters.palletId) {
            target += `&palletId=${this.linkItemFilters.palletId}`;
        }
        if (this.linkItemFilters.poNumber) {
            target += `&poNumber=${this.linkItemFilters.poNumber}`;
        }
        if (this.linkItemFilters.quantity) {
            target += `&quantity=${this.linkItemFilters.quantity}`;
        }
        if (this.linkItemFilters.receivedDate) {
            const dateData = this.linkItemFilters.receivedDate.split(" ~ ");
            if(!dateData[1] || dateData[1] === "null")
            {
                dateData[1] = dateData[0];
            }
            target += `&receivedDateStart=${dateData[0]}&receivedDateEnd=${dateData[1]}`;
        }
        if (this.linkItemFilters.returnReason) {
            target += `&returnReason=${this.linkItemFilters.returnReason}`;
        }
        if (this.linkItemFilters.returned !== undefined && this.linkItemFilters.returned != null) {
            target += `&returned=${this.linkItemFilters.returned}`;
        }
        if (this.linkItemFilters.serialNumber) {
            target += `&serialNumber=${this.linkItemFilters.serialNumber}`;
        }
        if (this.linkItemFilters.shrinkage !== undefined && this.linkItemFilters.shrinkage != null) {
            target += `&shrinkage=${this.linkItemFilters.shrinkage}`;
        }
        if (this.linkItemFilters.sku) {
            target += `&sku=${this.linkItemFilters.sku}`;
        }
        if (this.linkItemFilters.statuses) {
          this.linkItemFilters.statuses.forEach(element => {
            target += `&statuses=${element}`;
          });
        }
        if (this.linkItemFilters.warehouseLocationId != null) {
            target += `&warehouseLocationId=${this.linkItemFilters.warehouseLocationId}`;
        }
        if (this.linkItemFilters.isOpen !== undefined && this.linkItemFilters.isOpen != null) {
            target += `&isOpen=${this.linkItemFilters.isOpen}`;
        }
        if (this.linkItemFilters.isAvailable !== undefined && this.linkItemFilters.isAvailable != null) {
            target += `&isOpen=${this.linkItemFilters.isAvailable}`;
        }
        if (this.linkItemFilters.canBeAddedToOrder !== undefined && this.linkItemFilters.canBeAddedToOrder != null) {
            target += `&canBeAddedToOrder=${this.linkItemFilters.canBeAddedToOrder}`;
        }
      }

      if (this.linkItemDataSortKey) {
        target += `&sortField=${this.linkItemDataSortKey}&SortAscending=${
          this.linkItemDataSortAscending ? "true" : "false"
        }`;
      }
  
      try {
        var response = await rawApi.get<InventoryItemListItem[]>(target);
        this.setLinkItemList(response);
        this.setLinkItemCurrentPage(pageNumber);
      } catch (ex) {
        throw ex;
      } finally {
        this.setLoading(false);
      }
    }

    @Action
    async getPallets(params: GetPalletsRequest): Promise<void> {
      const pageSize = params ? params.pageSize : null || this.palletPageData.pageSize || 30;
      const pageNumber = params ? params.pageNumber : null || this.palletPageData.currentPage + 1;

      if (pageNumber === 1) {
        this.resetPalletList();
      }
  
      let target = `/barcode/pallets/?page=${pageNumber}&PageSize=${pageSize}`;
  
      if (this.palletFilters) {
        if (this.palletFilters.companyIds) {
          this.palletFilters.companyIds.forEach(element => {
            target += `&companyIds=${element}`;
          });
        }
        if (this.palletFilters.crn) {
            target += `&crn=${this.palletFilters.crn}`;
        }
        if (this.palletFilters.createdDate) {
            const dateData = this.palletFilters.createdDate.split(" ~ ");
            if(!dateData[1] || dateData[1] === "null")
            {
                dateData[1] = dateData[0];
            }
            target += `&createdDateStart=${dateData[0]}&createdDateEnd=${dateData[1]}`;
        }
        if (this.palletFilters.facilityCodes) {
          this.palletFilters.facilityCodes.forEach(element => {
            target += `&facilityCodes=${element}`;
          });
        }
        else if(barcodeSystemModuleState.currentFacilityCode)
        {
            target += `&facilityCodes=${barcodeSystemModuleState.currentFacilityCode}`;
        }
        if (this.palletFilters.id) {
            target += `&id=${this.palletFilters.id}`;
        }
        if (this.palletFilters.orderId) {
            target += `&orderId=${this.palletFilters.orderId}`;
        }
        if (this.palletFilters.orderNumber) {
            target += `&orderNumber=${this.palletFilters.orderNumber}`;
        }
        if (this.palletFilters.statuses) {
          this.palletFilters.statuses.forEach(element => {
            target += `&statuses=${element}`;
          });
        }
        if (this.palletFilters.warehouseLocationId != null) {
            target += `&warehouseLocationId=${this.palletFilters.warehouseLocationId}`;
        }
        if (this.palletFilters.hasItems !== undefined && this.palletFilters.hasItems != null) {
            target += `&hasItems=${this.palletFilters.hasItems}`;
        }
        if (this.palletFilters.isNoScanPallet !== undefined && this.palletFilters.isNoScanPallet != null) {
            target += `&isNoScanPallet=${this.palletFilters.isNoScanPallet}`;
        }
      }
      else if(barcodeSystemModuleState.currentFacilityCode)
      {
          target += `&facilityCodes=${barcodeSystemModuleState.currentFacilityCode}`;
      }

      if (this.palletDataSortKey) {
        target += `&sortField=${this.palletDataSortKey}&SortAscending=${
          this.palletDataSortAscending ? "true" : "false"
        }`;
      }
  
      try {
        var response = await rawApi.get<PalletListItem[]>(target);
        this.setPalletList(response);
        this.setPalletCurrentPage(pageNumber);
      } catch (ex) {
        throw ex;
      } finally {
        this.setLoading(false);
      }
    }

    @Action
    async getPalletsExport(params: GetPalletsRequest): Promise<void> {
      const pageSize = params ? params.pageSize : null || this.palletPageData.pageSize || 30;
      const pageNumber = params ? params.pageNumber : null || this.palletPageData.currentPage + 1;
  
      let target = `/barcode/pallets/?page=${pageNumber}&PageSize=${pageSize}`;
  
      if (this.palletFilters) {
        if (this.palletFilters.companyIds) {
          this.palletFilters.companyIds.forEach(element => {
            target += `&companyIds=${element}`;
          });
        }
        if (this.palletFilters.crn) {
            target += `&crn=${this.palletFilters.crn}`;
        }
        if (this.palletFilters.createdDate) {
            const dateData = this.palletFilters.createdDate.split(" ~ ");
            if(!dateData[1] || dateData[1] === "null")
            {
                dateData[1] = dateData[0];
            }
            target += `&createdDateStart=${dateData[0]}&createdDateEnd=${dateData[1]}`;
        }
        if (this.palletFilters.facilityCodes) {
          this.palletFilters.facilityCodes.forEach(element => {
            target += `&facilityCodes=${element}`;
          });
        }
        else if(barcodeSystemModuleState.currentFacilityCode)
        {
            target += `&facilityCodes=${barcodeSystemModuleState.currentFacilityCode}`;
        }
        if (this.palletFilters.id) {
            target += `&id=${this.palletFilters.id}`;
        }
        if (this.palletFilters.orderId) {
            target += `&orderId=${this.palletFilters.orderId}`;
        }
        if (this.palletFilters.orderNumber) {
            target += `&orderNumber=${this.palletFilters.orderNumber}`;
        }
        if (this.palletFilters.statuses) {
          this.palletFilters.statuses.forEach(element => {
            target += `&statuses=${element}`;
          });
        }
        if (this.palletFilters.warehouseLocationId != null) {
            target += `&warehouseLocationId=${this.palletFilters.warehouseLocationId}`;
        }
        if (this.palletFilters.hasItems !== undefined && this.palletFilters.hasItems != null) {
            target += `&hasItems=${this.palletFilters.hasItems}`;
        }
        if (this.palletFilters.isNoScanPallet !== undefined && this.palletFilters.isNoScanPallet != null) {
            target += `&isNoScanPallet=${this.palletFilters.isNoScanPallet}`;
        }
      }
      else if(barcodeSystemModuleState.currentFacilityCode)
      {
          target += `&facilityCodes=${barcodeSystemModuleState.currentFacilityCode}`;
      }

      if (this.palletDataSortKey) {
        target += `&sortField=${this.palletDataSortKey}&SortAscending=${
          this.palletDataSortAscending ? "true" : "false"
        }`;
      }
  
      try {
        var response = await rawApi.get<PalletListItem[]>(target);
        this.resetPalletList();
        this.setPalletList(response);
      } catch (ex) {
        throw ex;
      } finally {
        this.setLoading(false);
      }
    }


    
    @Action
    async getBulkInventoryItems(params: GetBulkInventoryItemsRequest): Promise<void> {
      const pageSize = params ? params.pageSize : null || this.bulkPageData.pageSize || 30;
      const pageNumber = params ? params.pageNumber : null || this.bulkPageData.currentPage + 1;

      if (pageNumber === 1) {
        this.resetBulkList();
      }
  
      let target = `/barcode/inventory/bulkInventoryItems/?page=${pageNumber}&PageSize=${pageSize}&hasQuantity=true`;
  
      if (this.bulkFilters) {
        if (this.bulkFilters.companyIds) {
          this.bulkFilters.companyIds.forEach(element => {
            target += `&companyIds=${element}`;
          });
        }
        if (this.bulkFilters.facilityCodes) {
          this.bulkFilters.facilityCodes.forEach(element => {
            target += `&facilityCodes=${element}`;
          });
        }
        else if(barcodeSystemModuleState.currentFacilityCode)
        {
            target += `&facilityCodes=${barcodeSystemModuleState.currentFacilityCode}`;
        }
        if (this.bulkFilters.id) {
            target += `&id=${this.bulkFilters.id}`;
        }
        if (this.bulkFilters.itemDescription) {
            target += `&itemDescription=${this.bulkFilters.itemDescription}`;
        }
        if (this.bulkFilters.itemType) {
            target += `&itemType=${this.bulkFilters.itemType}`;
        }
        if (this.bulkFilters.manufacturerCodes) {
          this.bulkFilters.manufacturerCodes.forEach(element => {
            target += `&manufacturerCodes=${element}`;
          });
        }
        if (this.bulkFilters.modelNumber) {
            target += `&modelNumber=${this.bulkFilters.modelNumber}`;
        }
        if (this.bulkFilters.quantity) {
            target += `&quantity=${this.bulkFilters.quantity}`;
        }
        if (this.bulkFilters.sku) {
            target += `&sku=${this.bulkFilters.sku}`;
        }
        if (this.bulkFilters.warehouseLocationId != null) {
            target += `&warehouseLocationId=${this.bulkFilters.warehouseLocationId}`;
        }
      }
      else if(barcodeSystemModuleState.currentFacilityCode)
      {
          target += `&facilityCodes=${barcodeSystemModuleState.currentFacilityCode}`;
      }

      if (this.bulkDataSortKey) {
        target += `&sortField=${this.bulkDataSortKey}&SortAscending=${
          this.bulkDataSortAscending ? "true" : "false"
        }`;
      }
  
      try {
        var response = await rawApi.get<BulkInventoryItemListItem[]>(target);
        this.setBulkList(response);
        this.setBulkCurrentPage(pageNumber);
      } catch (ex) {
        throw ex;
      } finally {
        this.setLoading(false);
      }
    }
    @Action
    async getBulkInventoryItemsExport(params: GetBulkInventoryItemsRequest): Promise<void> {
      const pageSize = params ? params.pageSize : null || this.bulkPageData.pageSize || 30;
      const pageNumber = params ? params.pageNumber : null || this.bulkPageData.currentPage + 1;

      let target = `/barcode/inventory/bulkInventoryItems/?page=${pageNumber}&PageSize=${pageSize}&hasQuantity=true`;
  
      if (this.bulkFilters) {
        if (this.bulkFilters.companyIds) {
          this.bulkFilters.companyIds.forEach(element => {
            target += `&companyIds=${element}`;
          });
        }
        if (this.bulkFilters.facilityCodes) {
          this.bulkFilters.facilityCodes.forEach(element => {
            target += `&facilityCodes=${element}`;
          });
        }
        else if(barcodeSystemModuleState.currentFacilityCode)
        {
            target += `&facilityCodes=${barcodeSystemModuleState.currentFacilityCode}`;
        }
        if (this.bulkFilters.id) {
            target += `&id=${this.bulkFilters.id}`;
        }
        if (this.bulkFilters.itemDescription) {
            target += `&itemDescription=${this.bulkFilters.itemDescription}`;
        }
        if (this.bulkFilters.itemType) {
            target += `&itemType=${this.bulkFilters.itemType}`;
        }
        if (this.bulkFilters.manufacturerCodes) {
          this.bulkFilters.manufacturerCodes.forEach(element => {
            target += `&manufacturerCodes=${element}`;
          });
        }
        if (this.bulkFilters.modelNumber) {
            target += `&modelNumber=${this.bulkFilters.modelNumber}`;
        }
        if (this.bulkFilters.quantity) {
            target += `&quantity=${this.bulkFilters.quantity}`;
        }
        if (this.bulkFilters.sku) {
            target += `&sku=${this.bulkFilters.sku}`;
        }
        if (this.bulkFilters.warehouseLocationId != null) {
            target += `&warehouseLocationId=${this.bulkFilters.warehouseLocationId}`;
        }
      }
      else if(barcodeSystemModuleState.currentFacilityCode)
      {
          target += `&facilityCodes=${barcodeSystemModuleState.currentFacilityCode}`;
      }

      if (this.bulkDataSortKey) {
        target += `&sortField=${this.bulkDataSortKey}&SortAscending=${
          this.bulkDataSortAscending ? "true" : "false"
        }`;
      }
  
      try {
        var response = await rawApi.get<BulkInventoryItemListItem[]>(target);
        this.resetBulkList();
        this.setBulkList(response);
      } catch (ex) {
        throw ex;
      } finally {
        this.setLoading(false);
      }
    }

    @Action
    async getPalletItems(){
      this.resetPalletItems();
      if(this.currentPallet && this.currentPallet.id) {
        try {
          var response = await rawApi.get<InventoryItemListItem[]>(`/barcode/pallets/${this.currentPallet.id}/inventoryItems`);
          this.setPalletItemsList(response.data);
        } catch (ex) {
          throw ex;
        } finally {
          this.setLoading(false);
        }
      }
    }

    @Action
    async getPalletOrders(){
      this.resetPalletOrders();
      if(this.currentPallet && this.currentPallet.id) {
        try {
          var response = await rawApi.get<BarcodeOrderListItem[]>(`/barcode/pallets/${this.currentPallet.id}/orders`);
          this.setPalletOrdersList(response.data);
        } catch (ex) {
          throw ex;
        } finally {
          this.setLoading(false);
        }
      }
    }

    @Action
    async getInventoryTransactionsForInventoryItem(){
      this.resetInventoryTransactions();
      if(this.currentInventoryItem && this.currentInventoryItem.id) {
        try {
          var response = await rawApi.get<InventoryTransactionListItem[]>(`/barcode/inventory/inventoryItems/${this.currentInventoryItem.id}/inventoryTransactions`);
          this.setTransList(response.data);
        } catch (ex) {
          throw ex;
        } finally {
          this.setLoading(false);
        }
      }
    }

    @Action
    async getInventoryTransactionsForPallet(){
      this.resetInventoryTransactions();
      if(this.currentPallet && this.currentPallet.id) {
        try {
          var response = await rawApi.get<InventoryTransactionListItem[]>(`/barcode/pallets/${this.currentPallet.id}/inventoryTransactions`);
          this.setTransList(response.data);
        } catch (ex) {
          throw ex;
        } finally {
          this.setLoading(false);
        }
      }
    }

    @Action
    async getOtherBulkInventoryItems(){
      this.resetOtherBulkInventoryItems();
      if(this.currentBulkInventoryItem && this.currentBulkInventoryItem.id) {
        try {
          var response = await rawApi.get<BulkInventoryItemListItem[]>(`/barcode/inventory/bulkInventoryItems/?hasQuantity=true&companyIds=${this.currentBulkInventoryItem.companyId}&manufacturerCode=${this.currentBulkInventoryItem.manufacturerCode}&modelNumber=${this.currentBulkInventoryItem.modelNumber}&facilityCodes=${this.currentBulkInventoryItem.facilityCode}`);
          this.setOtherBulkInventoryItems(response.data);
        } catch (ex) {
          throw ex;
        } finally {
          this.setLoading(false);
        }
      }
    }

    @Action
    resetInventoryTransactions(){
      this.setTransList(null);
    }
    
    @Action
    resetOtherBulkInventoryItems(){
      this.setOtherBulkInventoryItems(null);
    }
    @Action
    resetPalletItems(){
      this.setPalletItemsList(null);
    }
    @Action
    resetPalletOrders(){
      this.setPalletOrdersList(null);
    }

    @Action
    async getInventoryItem(id): Promise<InventoryItemResource> {
      const response = await rawApi.get<InventoryItemResource>(`/barcode/inventory/inventoryItems/${id}`);
      return response.data;
    }

    @Action
    async getAvailableQuantity(request: AvailableQuantityRequest): Promise<number> {
      const response = await rawApi.get<AvailableQuantityResponse>(`/barcode/inventory/availableQuantity/?facilityCode=${request.facilityCode}&companyId=${request.companyId}&manufacturerCode=${request.manufacturerCode}&modelNumber=${request.modelNumber}&poNumber=${request.poNumber}`);
      return response.data.availableQuantity;
    }

    @Action
    async getPallet(id): Promise<PalletResource> {
      const response = await rawApi.get<PalletResource>(`/barcode/pallets/${id}`);
      return response.data;
    }

    @Action
    async getBulkInventoryItem(id): Promise<BulkInventoryItemResource> {
      const response = await rawApi.get<BulkInventoryItemResource>(`/barcode/inventory/bulkInventoryItems/${id}`);
      return response.data;
    }

    @Action
    async getModelCubes(payload): Promise<ModelCubesResource[]> {
      let target = `/barcode/inventory/modelCubes?nullCubesOnly=${payload.nullCubesOnly}`;

      if(payload.companyId)
        target = target + `&companyId=${payload.companyId}`;

      if(payload.manufacturerCode)
        target = target + `&manufacturerCode=${payload.manufacturerCode}`;

      if(payload.modelNumber)
        target = target + `&modelNumber=${payload.modelNumber}`;

      const response = await rawApi.get<ModelCubesResource[]>(target);

      return response.data;
    }

    @Action
    async getModelCube(payload): Promise<ModelCubesResource> {
      const response = await rawApi.get<ModelCubesResource>(`/barcode/inventory/modelCubes/${payload.manufacturerCode}/${payload.modelNumber}`);
      return response.data;
    }    
    
    @Action
    async getModelLinkSumamry(payload): Promise<ModelLinkSummaryResource> {
      let target = `/barcode/inventory/modelLinkSummary/?facilityCode=${payload.facilityCode}&companyId=${payload.companyId}&modelNumber=${payload.modelNumber}`;

      if(payload.manufacturerCode)
        target = target + `&manufacturerCode=${payload.manufacturerCode}`;
   
      if(payload.poNumber)
        target = target + `&poNumber=${payload.poNumber}`;
      
      const response = await rawApi.get<ModelLinkSummaryResource>(target);
      return response.data;
    }

    @Action
    async applySummaryListSort(sort: string) {     
      this.setSummaryDataSort(sort);
      await this.getInventorySummary({
          pageNumber: 1,
          pageSize: this.summaryPageData.pageSize
      });
    }

    @Action
    async applyItemListSort(sort: string) {     
      this.setItemDataSort(sort);
      await this.getInventoryItems({
          pageNumber: 1,
          pageSize: this.itemPageData.pageSize
      });
    }

    @Action
    async applyLinkItemListSort(sort: string) {     
      this.setLinkItemDataSort(sort);
      await this.getInventoryItemsForLinking({
          pageNumber: 1,
          pageSize: this.linkItemPageData.pageSize
      });
    }

    @Action
    async applyBulkListSort(sort: string) {     
      this.setBulkDataSort(sort);
      await this.getBulkInventoryItems({
          pageNumber: 1,
          pageSize: this.bulkPageData.pageSize
      });
    }

    @Action
    async applyPalletSort(sort: string) {     
      this.setPalletDataSort(sort);
      await this.getPallets({
          pageNumber: 1,
          pageSize: this.palletPageData.pageSize
      });
    }

    @Action
    toggleSummaryField(summaryFieldChange) {
      this.setSummaryFieldStatus(summaryFieldChange);
    }

    @Action
    toggleItemField(itemFieldChange) {
      this.setItemFieldStatus(itemFieldChange);
    }

    @Action
    toggleBulkField(bulkFieldChange) {
      this.setBulkFieldStatus(bulkFieldChange);
    }

    @Action
    togglePalletField(palletFieldChange) {
      this.setPalletFieldStatus(palletFieldChange);
    }

    @Action 
    resetData() {
        this.resetSummaryList();
        this.resetItemList();
        this.resetLinkItemList();
        this.resetPalletList();
        this.resetInventoryTransactions();
        this.resetPalletItems();
        this.resetPalletOrders();
        this.resetBulkList();
        this.resetOtherBulkInventoryItems();
    }

    @Action
    async loadInventoryItem(id: number): Promise<void> {
      const data = await this.getInventoryItem(id);
      this.setCurrentInventoryItem(data);
      this.refreshInventoryItemData(data);
    }     
    
    @Action
    async reloadInventoryItem(): Promise<void> {
      if (this.currentInventoryItem){
        const data = await this.getInventoryItem(this.currentInventoryItem.id);
        this.setCurrentInventoryItem(data);
        this.refreshInventoryItemData(data);
      }
    } 

    @Action
    async loadBulkInventoryItem(id: number): Promise<void> {
      const data = await this.getBulkInventoryItem(id);
      this.setCurrentBulkInventoryItem(data);
      this.refreshBulkInventoryItemData(data);
    }     
    
    @Action
    async reloadBulkInventoryItem(): Promise<void> {
      if (this.currentBulkInventoryItem){
        const data = await this.getBulkInventoryItem(this.currentBulkInventoryItem.id);
        this.setCurrentBulkInventoryItem(data);
        this.refreshBulkInventoryItemData(data);
      }
    } 

    @Action
    async loadPallet(id: string): Promise<void> {
      this.resetPalletItems();
      this.resetPalletOrders();
      const data = await this.getPallet(id);
      this.setCurrentPallet(data);
      this.refreshPalletData(data);
    }     
    
    @Action
    async reloadPallet(): Promise<void> {
      if (this.currentPallet){
        this.resetPalletItems();
        this.resetPalletOrders();
        const data = await this.getPallet(this.currentPallet.id);
        this.setCurrentPallet(data);
        this.refreshPalletData(data);
      }
    } 

    @Action
    async loadPalletItems(): Promise<void> {
      this.resetPalletItems();
      await this.getPalletItems();
    }  

    @Action
    async loadPalletOrders(): Promise<void> {
      this.resetPalletOrders();
      await this.getPalletOrders();
    }   

    @Action
    async viewInventoryItemShortcutAction(payload) {
      if (!payload || !payload.selectedItems) {
        return;
      }
      return this.context.dispatch(
        "system/showModal",
        { name: modals.inventoryItemViewModal, data: { selectedItems: payload.selectedItems } },
        { root: true }
      );
    }

    @Action
    async resolveMissingInventoryItemShortcutAction(payload) {
      if (!payload || !payload.selectedItems) {
        return;
      }
      return this.context.dispatch(
        "system/showModal",
        { name: modals.resolveMissingInventoryItemModal, data: { selectedItems: payload.selectedItems } },
        { root: true }
      );
    }

    @Action
    async viewBulkInventoryItemShortcutAction(payload) {
      if (!payload || !payload.selectedItems) {
        return;
      }
      return this.context.dispatch(
        "system/showModal",
        { name: modals.bulkInventoryItemViewModal, data: { selectedItems: payload.selectedItems } },
        { root: true }
      );
    }

    @Action
    async viewModelLinkSummaryShortcutAction(payload) {
      if (!payload || !payload.selectedItems) {
        return;
      }
      return this.context.dispatch(
        "system/showModal",
        { name: modals.modelLinkSummaryModal, data: { selectedItems: payload.selectedItems } },
        { root: true }
      );
    }

    @Action
    async viewPalletShortcutAction(payload) {
      if (!payload || !payload.selectedItems) {
        return;
      }
      return this.context.dispatch(
        "system/showModal",
        { name: modals.palletViewModal, data: { selectedItems: payload.selectedItems } },
        { root: true }
      );
    }      

    @Action
    async updateInventoryItem(request: UpdateInventoryItemRequest) {
      await api.putAsync(`/barcode/inventory/inventoryItems/${request.id}`, request);
    } 

    @Action
    async resolveMissingInventoryItem(request: ResolveMissingInventoryItemRequest) {
      await api.postAsync(`/barcode/inventory/inventoryItems/${request.id}/resolve`, request);
    }     
    
    @Action
    async deleteInventoryItem(request: DeleteInventoryItemRequest) {
      await api.deleteAsync(`/barcode/inventory/inventoryItems/${request.id}?deleteReason=${request.deleteReason}`);
    }     
    
    @Action
    async saveModelCubes(request: SaveCubesRequest) {
      await api.postAsync(`/barcode/inventory/modelCubes/save`, request);
    } 

    @Mutation
    setLoading(isLoading: boolean) {
        this.loading = isLoading;
    }

    @Mutation
    setSummaryFilters(filters: InventorySummaryListFilters)
    {
        this.summaryFilters = filters;
    }

    @Mutation
    setItemFilters(filters: InventoryItemListFilters)
    {
        this.itemFilters = filters;
    }

    @Mutation
    setLinkItemFilters(filters: InventoryItemListFilters)
    {
        this.linkItemFilters = filters;
    }
    
    @Mutation
    setBulkFilters(filters: BulkInventoryItemListFilters)
    {
        this.bulkFilters = filters;
    }

    @Mutation
    setPalletFilters(filters: PalletListFilters)
    {
        this.palletFilters = filters;
    }

    @Mutation
    setSummaryList(response: AxiosResponse<InventorySummaryListItem[]>) {
        this.summaryPageData.totalItems = response.headers.totalitems;
        this.summaryPageData.totalPages = response.headers.totalpages;
        if (this.summaryData) {
            this.summaryData = this.summaryData.concat(response.data);
        } else {
            this.summaryData = response.data;
        }
    }  

    @Mutation
    setItemList(response: AxiosResponse<InventoryItemListItem[]>) {
        this.itemPageData.totalItems = response.headers.totalitems;
        this.itemPageData.totalPages = response.headers.totalpages;
        if (this.itemData) {
            this.itemData = this.itemData.concat(response.data);
        } else {
            this.itemData = response.data;
        }
    }

    @Mutation
    setLinkItemList(response: AxiosResponse<InventoryItemListItem[]>) {
        this.linkItemPageData.totalItems = response.headers.totalitems;
        this.linkItemPageData.totalPages = response.headers.totalpages;
        if (this.linkItemData) {
            this.linkItemData = this.linkItemData.concat(response.data);
        } else {
            this.linkItemData = response.data;
        }
    }

    @Mutation
    setBulkList(response: AxiosResponse<BulkInventoryItemListItem[]>) {
        this.bulkPageData.totalItems = response.headers.totalitems;
        this.bulkPageData.totalPages = response.headers.totalpages;
        if (this.bulkData) {
            this.bulkData = this.bulkData.concat(response.data);
        } else {
            this.bulkData = response.data;
        }
    }
   
    @Mutation
    setPalletList(response: AxiosResponse<PalletListItem[]>) {
        this.palletPageData.totalItems = response.headers.totalitems;
        this.palletPageData.totalPages = response.headers.totalpages;
        if (this.palletData) {
            this.palletData = this.palletData.concat(response.data);
        } else {
            this.palletData = response.data;
        }
    }

    @Mutation
    setTransList(data: InventoryTransactionListItem[]) {
      this.transData = data;
    }    
    
    @Mutation
    setOtherBulkInventoryItems(data: BulkInventoryItemListItem[]) {
      if(data && data.length > 0 && this.currentBulkInventoryItem)
        this.otherBulkInventoryItems = data.filter(x => x.id != this.currentBulkInventoryItem.id);
      else
        this.otherBulkInventoryItems = data;
    }
    
    @Mutation
    setPalletItemsList(data: InventoryItemListItem[]) {
      this.palletItemsData = data;
    } 
    
    @Mutation
    setPalletOrdersList(data: BarcodeOrderListItem[]) {
      this.palletOrdersData = data;
    } 
 
    @Mutation
    setSummaryCurrentPage(number: number) {
      this.summaryPageData.currentPage = number;
    }    
    
    @Mutation
    setItemCurrentPage(number: number) {
      this.itemPageData.currentPage = number;
    }
    
    @Mutation
    setLinkItemCurrentPage(number: number) {
      this.linkItemPageData.currentPage = number;
    }  
    
    @Mutation
    setBulkCurrentPage(number: number) {
      this.bulkPageData.currentPage = number;
    }    
    
    @Mutation
    setPalletCurrentPage(number: number) {
      this.palletPageData.currentPage = number;
    }

    @Mutation
    resetSummaryList() {
      this.summaryPageData.totalItems = 0;
      this.summaryPageData.totalPages = 0;
      this.summaryData = null;
    }

    @Mutation
    resetItemList() {
      this.itemPageData.totalItems = 0;
      this.itemPageData.totalPages = 0;
      this.itemData = null;
    }

    @Mutation
    resetLinkItemList() {
      this.linkItemPageData.totalItems = 0;
      this.linkItemPageData.totalPages = 0;
      this.linkItemData = null;
    }

    @Mutation
    resetBulkList() {
      this.bulkPageData.totalItems = 0;
      this.bulkPageData.totalPages = 0;
      this.bulkData = null;
    }

    @Mutation
    resetPalletList() {
      this.palletPageData.totalItems = 0;
      this.palletPageData.totalPages = 0;
      this.palletData = null;
    }

    @Mutation
    switchSummarySelectionStatus(summaryId: string) {
      var index = this.summaryData.findIndex(function(item) {
          return item.id === summaryId;
      });

      if (index < 0) {
          return;
      }

      const existing = this.summaryData[index];

      existing.selected = !existing.selected;

      this.summaryData = [...this.summaryData.slice(0, index), existing, ...this.summaryData.slice(index + 1)];
    }


    @Mutation
    switchItemSelectionStatus(id: number) {
      var index = this.itemData.findIndex(function(item) {
          return item.id === id;
      });

      if (index < 0) {
          return;
      }

      const existing = this.itemData[index];

      existing.selected = !existing.selected;

      this.itemData = [...this.itemData.slice(0, index), existing, ...this.itemData.slice(index + 1)];
    }

    @Mutation
    switchLinkItemSelectionStatus(id: number) {
      var index = this.linkItemData.findIndex(function(item) {
          return item.id === id;
      });

      if (index < 0) {
          return;
      }

      const existing = this.linkItemData[index];

      existing.selected = !existing.selected;

      this.linkItemData = [...this.linkItemData.slice(0, index), existing, ...this.linkItemData.slice(index + 1)];
    }

    @Mutation
    switchBulkItemSelectionStatus(id: number) {
      var index = this.bulkData.findIndex(function(item) {
          return item.id === id;
      });

      if (index < 0) {
          return;
      }

      const existing = this.bulkData[index];

      existing.selected = !existing.selected;

      this.bulkData = [...this.bulkData.slice(0, index), existing, ...this.bulkData.slice(index + 1)];
    }

    @Mutation
    switchPalletSelectionStatus(id: string) {
        var index = this.palletData.findIndex(function(pallet) {
            return pallet.id === id;
        });

        if (index < 0) {
            return;
        }

        const existing = this.palletData[index];

        existing.selected = !existing.selected;

        this.palletData = [...this.palletData.slice(0, index), existing, ...this.palletData.slice(index + 1)];
    }

    @Mutation
    switchPalletItemSelectionStatus(id: number) {
        var index = this.palletItemsData.findIndex(function(item) {
            return item.id === id;
        });

        if (index < 0) {
            return;
        }

        const existing = this.palletItemsData[index];

        existing.selected = !existing.selected;

        this.palletItemsData = [...this.palletItemsData.slice(0, index), existing, ...this.palletItemsData.slice(index + 1)];
    }

    @Mutation
    switchPalletOrderSelectionStatus(id: number) {
        var index = this.palletOrdersData.findIndex(function(order) {
            return order.id === id;
        });

        if (index < 0) {
            return;
        }

        const existing = this.palletOrdersData[index];

        existing.selected = !existing.selected;

        this.palletOrdersData = [...this.palletOrdersData.slice(0, index), existing, ...this.palletOrdersData.slice(index + 1)];
    }

    @Mutation
    switchOtherBulkInventoryItemSelectionStatus(id: number) {
        var index = this.otherBulkInventoryItems.findIndex(function(item) {
            return item.id === id;
        });

        if (index < 0) {
            return;
        }

        const existing = this.otherBulkInventoryItems[index];

        existing.selected = !existing.selected;

        this.otherBulkInventoryItems = [...this.otherBulkInventoryItems.slice(0, index), existing, ...this.otherBulkInventoryItems.slice(index + 1)];
    }

    @Mutation
    setSummaryFieldStatus(fieldChange) {
      this.summaryFields[fieldChange.idx].hidden = fieldChange.value;
    }

    @Mutation
    setItemFieldStatus(fieldChange) {
      this.itemFields[fieldChange.idx].hidden = fieldChange.value;
    }

    @Mutation
    setBulkFieldStatus(fieldChange) {
      this.bulkFields[fieldChange.idx].hidden = fieldChange.value;
    }

    @Mutation
    setPalletFieldStatus(fieldChange) {
      this.palletFields[fieldChange.idx].hidden = fieldChange.value;
    }

    @Mutation
    setSummaryDataSort(key) {
      if (this.summaryDataSortKey === key) this.summaryDataSortAscending = !this.summaryDataSortAscending;
      else {
        this.summaryDataSortKey = key;
        this.summaryDataSortAscending = true;
      }
    }

    @Mutation
    setItemDataSort(key) {
      if (this.itemDataSortKey === key) this.itemDataSortAscending = !this.itemDataSortAscending;
      else {
        this.itemDataSortKey = key;
        this.itemDataSortAscending = true;
      }
    } 
    
    @Mutation
    setLinkItemDataSort(key) {
      if (this.linkItemDataSortKey === key) this.linkItemDataSortAscending = !this.linkItemDataSortAscending;
      else {
        this.linkItemDataSortKey = key;
        this.linkItemDataSortAscending = true;
      }
    }     
    
    @Mutation
    setBulkDataSort(key) {
      if (this.bulkDataSortKey === key) this.bulkDataSortAscending = !this.bulkDataSortAscending;
      else {
        this.bulkDataSortKey = key;
        this.bulkDataSortAscending = true;
      }
    }

    @Mutation
    setPalletDataSort(key) {
      if (this.palletDataSortKey === key) this.palletDataSortAscending = !this.palletDataSortAscending;
      else {
        this.palletDataSortKey = key;
        this.palletDataSortAscending = true;
      }
    }

    @Mutation
    setCurrentInventoryItem(data) {
      this.loading = false;
      this.currentInventoryItem = data;
    }  

    @Mutation
    setCurrentBulkInventoryItem(data) {
      this.loading = false;
      this.currentBulkInventoryItem = data;
    }  

    @Mutation
    setCurrentPallet(data) {
      this.loading = false;
      this.currentPallet = data;
    }  
    
    @Mutation
    refreshInventoryItemData(inventoryItem : InventoryItemListItem) {
      if(this.itemData)
      {
        var index = this.itemData.findIndex(function(item) {
            return item.id === inventoryItem.id;
        });

        if (index < 0) {
            return;
        }

        const copyItem = JSON.parse(JSON.stringify(inventoryItem));

        this.itemData = [...this.itemData.slice(0, index), copyItem, ...this.itemData.slice(index + 1)]
      }
      if(this.linkItemData)
      {
        var index = this.linkItemData.findIndex(function(item) {
            return item.id === inventoryItem.id;
        });

        if (index < 0) {
            return;
        }

        const copyItem = JSON.parse(JSON.stringify(inventoryItem));

        this.linkItemData = [...this.linkItemData.slice(0, index), copyItem, ...this.linkItemData.slice(index + 1)]
      }
    } 

    @Mutation
    refreshBulkInventoryItemData(bulkInventoryItem : BulkInventoryItemListItem) {
      if(this.bulkData)
      {
        var index = this.bulkData.findIndex(function(item) {
            return item.id === bulkInventoryItem.id;
        });

        if (index < 0) {
            return;
        }

        const copyItem = JSON.parse(JSON.stringify(bulkInventoryItem));

        this.bulkData = [...this.bulkData.slice(0, index), copyItem, ...this.bulkData.slice(index + 1)]
      }
    } 
    
    @Mutation
    refreshPalletData(pallet : PalletListItem) {
      if(this.palletData)
      {
        var index = this.palletData.findIndex(function(item) {
            return item.id === pallet.id;
        });

        if (index < 0) {
            return;
        }

        const copyItem = JSON.parse(JSON.stringify(pallet));

        this.palletData = [...this.palletData.slice(0, index), copyItem, ...this.palletData.slice(index + 1)]
      }
    } 

    @Mutation
    removeInventoryItemData(inventoryItem : InventoryItemListItem) {
      if(this.itemData)
      {
        var index = this.itemData.findIndex(function(item) {
            return item.id === inventoryItem.id;
        });

        if (index < 0) {
            return;
        }

        this.itemData = [...this.itemData.slice(0, index), ...this.itemData.slice(index + 1)]
      }
    } 

    @Mutation
    setItemCompanyId(id: number) {
      if(this.currentInventoryItem)
        this.currentInventoryItem.companyId = id;
    }

    @Mutation
    setItemManufacturerCode(code: string) {
      if(this.currentInventoryItem)
        this.currentInventoryItem.manufacturerCode = code;
    }

    @Mutation
    setItemType(type: string) {
      if(this.currentInventoryItem)
        this.currentInventoryItem.itemType = type;
    }

    @Mutation
    setItemPoNumber(poNumber: string) {
      if(this.currentInventoryItem)
        this.currentInventoryItem.poNumber = poNumber;
    }

    @Mutation
    setItemModelNumber(modelNumber: string) {
      if(this.currentInventoryItem)
        this.currentInventoryItem.modelNumber = modelNumber;
    }

    @Mutation
    setItemSku(sku: string) {
      if(this.currentInventoryItem)
        this.currentInventoryItem.sku = sku;
    }

    @Mutation
    setItemSerialNumber(serialNumber: string) {
      if(this.currentInventoryItem)
        this.currentInventoryItem.serialNumber = serialNumber;
    }

    @Mutation
    setItemDescription(itemDescription: string) {
      if(this.currentInventoryItem)
        this.currentInventoryItem.itemDescription = itemDescription;
    }

    @Mutation
    setItemQuantity(quantity: number) {
      if(this.currentInventoryItem)
        this.currentInventoryItem.quantity = quantity;
    }

    @Mutation
    setItemDamaged(isDamaged: boolean) {
      if(this.currentInventoryItem)
        this.currentInventoryItem.isDamaged = isDamaged;
    }

    @Mutation
    setItemDamageCode(damageCode: string) {
      if(this.currentInventoryItem)
        this.currentInventoryItem.damageCode = damageCode;
    }

    @Mutation
    setItemReturned(returned: boolean) {
      if(this.currentInventoryItem)
        this.currentInventoryItem.returned = returned;
    }

    @Mutation
    setItemReturnReason(returnReason: string) {
      if(this.currentInventoryItem)
        this.currentInventoryItem.returnReason = returnReason;
    }

    @Mutation
    setItemReturnComment(returnComment: string) {
      if(this.currentInventoryItem)
        this.currentInventoryItem.returnComment = returnComment;
    }

    @Mutation
    setItemRaNumber(raNumber: string) {
      if(this.currentInventoryItem)
        this.currentInventoryItem.raNumber = raNumber;
    }
    
    @Mutation
    setItemAuctionNumber(auctionNumber: string) {
      if(this.currentInventoryItem)
        this.currentInventoryItem.auctionNumber = auctionNumber;
    }
    
    @Mutation
    setItemDisposalNumber(disposalNumber: string) {
      if(this.currentInventoryItem)
        this.currentInventoryItem.disposalNumber = disposalNumber;
    }
    
    @Mutation
    setItemShrinkage(shrinkage: boolean) {
      if(this.currentInventoryItem)
        this.currentInventoryItem.shrinkage = shrinkage;
    }
    
    get getSelectedSummaryItems(): Array<InventorySummaryListItem> {
        if (!this.summaryData) return [];
        return this.summaryData.filter(x => x.selected);
    }

    get getSelectedSummaryItemCount(): number {
        return this.getSelectedSummaryItems.length;
    }

    get getSelectedItems(): Array<InventoryItemListItem> {
        if (!this.itemData) return [];
        return this.itemData.filter(x => x.selected);
    }

    get getSelectedItemCount(): number {
        return this.getSelectedItems.length;
    }

    get getSelectedLinkItems(): Array<InventoryItemListItem> {
        if (!this.linkItemData) return [];
        return this.linkItemData.filter(x => x.selected);
    }

    get getSelectedLinkItemCount(): number {
        return this.getSelectedLinkItems.length;
    }

    get getSelectedPallets(): Array<PalletListItem> {
        if (!this.palletData) return [];
        return this.palletData.filter(x => x.selected);
    }

    get getSelectedPalletCount(): number {
        return this.getSelectedPallets.length;
    }

    get getSelectedPalletItems(): Array<InventoryItemListItem> {
      if (!this.palletItemsData) return [];
      return this.palletItemsData.filter(x => x.selected);
    }

    get getSelectedPalletItemCount(): number {
        return this.getSelectedPalletItems.length;
    }

    get getSelectedPalletOrders(): Array<BarcodeOrderListItem> {
      if (!this.palletOrdersData) return [];
      return this.palletOrdersData.filter(x => x.selected);
    }

    get getSelectedPalletOrderCount(): number {
        return this.getSelectedPalletOrders.length;
    }

    get getSelectedBulkItems(): Array<BulkInventoryItemListItem> {
      if (!this.bulkData) return [];
      return this.bulkData.filter(x => x.selected);
    } 

    get getSelectedBulkItemCount(): number {
      return this.getSelectedBulkItems.length;
    }

    get getSelectedOtherBulkInventoryItems(): Array<BulkInventoryItemListItem> {
      if (!this.otherBulkInventoryItems) return [];
      return this.otherBulkInventoryItems.filter(x => x.selected);
    } 

    get getSelectedOtherBulkInventoryItemCount(): number {
      return this.getSelectedOtherBulkInventoryItems.length;
    }
}